import InfoRounded from '@mui/icons-material/InfoRounded';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { DrawerActions } from '@openx/components-core/lib/Drawer/DrawerActionBar.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type UseGetActionsProps =
  | {
      shouldDisplayResetButton: true;
      bulk: {
        isValidating: boolean;
        isResetEnabled: boolean;
      };
      onReset: () => void;
      onValidate: (hasInventoryListChanged: boolean) => void;
    }
  | {
      shouldDisplayResetButton: false;
      bulk: {
        isValidating: boolean;
      };
      onValidate: (hasInventoryListChanged: boolean) => void;
    };

const DomainsTooltip = () => {
  const { t } = useTranslation();

  return (
    <Tooltip
      placement="top"
      title={t(
        'Standard domain variations like http, https, and www are treated as the same domain. Duplicates will be cleaned up automatically during the validation process'
      )}
    >
      <IconButton data-test="tooltip-icon">
        <InfoRounded />
      </IconButton>
    </Tooltip>
  );
};

export const useGetActions = (props: UseGetActionsProps) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const {
      bulk: { isValidating },
      onValidate,
    } = props;

    const initActions: DrawerActions = [
      {
        label: t('Validate'),
        loading: isValidating,
        onClick: () => onValidate(false),
      },
      {
        label: 'validation-tooltip',
        customRender: () => <DomainsTooltip />,
      },
    ];

    if (props.shouldDisplayResetButton) {
      const {
        onReset,
        bulk: { isResetEnabled },
      } = props;

      initActions.push({
        color: 'error',
        'data-test': 'reset-to-account-settings',
        disabled: !isResetEnabled,
        label: t("Reset to account's settings"),
        loading: isValidating,
        onClick: onReset,
      });
    }

    return initActions;
  }, [t, props]);
};
